import {
  getSnowflakeSessionEndpoint,
  getCortexAgentSessionRunEndpoint,
  getCortexThreadCreateEndpoint,
} from "../configuration";
import store from "../store";

/**
 * Get auth headers for fetch() calls (bypasses axios interceptor).
 * Mirrors the token injection from axiosInterceptor.jsx.
 */
function getAuthHeaders() {
  const activeSession = store?.getState()?.sessionReducer?.activeSession?.activeSession;
  // Redis mode: session ID in httpOnly cookie, middleware extracts token from Redis
  // Legacy mode: sessionToken (Bearer token)
  const accessToken = activeSession?.sessionToken;
  const transactionId = crypto.randomUUID();
  const headers = {
    "Content-Type": "application/json",
    "X-Transaction-ID": transactionId,
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  return headers;
}

// ─── Snowflake Session Cache ──────────────────────────────────────────────────
//
// sf-manager 2.1.0: the Snowflake session is a Redis-backed HttpOnly cookie
// (sf:vision:session-id, Secure, SameSite=Strict). POST /api/v1/snowflake/session
// returns only { expiresAt }; no token is issued to the browser.
// We cache expiresAt per account so we can avoid creating a new session on every
// message. A 5-minute buffer triggers proactive renewal before the session expires.

let sessionCache = { expiresAt: null, accountname: null };

const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

/**
 * Ensures a valid Snowflake session cookie exists for the given account.
 * Creates a new session if none is cached or if it is within 5 min of expiry.
 * The actual session ID travels as an HttpOnly cookie set by sf-manager.
 * Cookies are sent automatically for same-origin requests — no credentials
 * option needed.
 *
 * @param {string} accountname - billing account ID
 * @throws {Error} on non-OK HTTP status (including 503 when the vision agent
 *   user is not configured in sf-manager)
 */
export async function fetchSnowflakeSessionToken(accountname) {
  const now = Date.now();

  if (
    sessionCache.accountname === accountname &&
    sessionCache.expiresAt &&
    now < new Date(sessionCache.expiresAt).getTime() - EXPIRY_BUFFER_MS
  ) {
    return; // valid session cookie already in place
  }

  const response = await fetch(getSnowflakeSessionEndpoint(), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ accountname }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Session token error ${response.status}: ${text}`);
  }

  const data = await response.json();

  // Fallback: if backend doesn't return expiresAt, use 30-min default TTL
  const DEFAULT_TTL_MS = 30 * 60 * 1000;
  const expiryTime = data.expiresAt ? new Date(data.expiresAt).getTime() : Date.now() + DEFAULT_TTL_MS;

  sessionCache = {
    expiresAt: new Date(expiryTime).toISOString(),
    accountname,
  };
}

/**
 * Clears the cached session expiry (e.g., on logout, account switch, or 401).
 */
export function clearSessionTokenCache() {
  sessionCache = { expiresAt: null, accountname: null };
}

// ─── Cortex Thread Cache ────────────────────────────────────────────────────────
//
// Thread IDs are created per account after successful session establishment.
// Cache structure: { threadId: string, accountname: string, createdAt: number }
// Threads do not expire in this implementation; a new thread is created only when
// none exists for the account.

let threadCache = { threadId: null, accountname: null, createdAt: null };

/**
 * Creates a new Cortex Agent thread for the given account.
 * Threads are reused per account; a new thread is created only if none is cached.
 * Thread creation must occur AFTER a successful Snowflake session is established.
 *
 * @param {string} accountname - billing account ID
 * @returns {Promise<string>} the thread_id
 * @throws {Error} on non-OK HTTP status
 */
export async function createCortexThread(accountname) {
  // Return cached thread if it exists for this account
  if (threadCache.accountname === accountname && threadCache.threadId) {
    return threadCache.threadId;
  }

  const response = await fetch(getCortexThreadCreateEndpoint(), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ accountname }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Cortex thread creation error ${response.status}: ${text}`);
  }

  const data = await response.json();
  // Store as string to preserve precision (JavaScript loses precision > 2^53)
  const threadId = String(data.thread_id);

  // Cache the thread for this account
  threadCache = {
    threadId,
    accountname,
    createdAt: Date.now(),
  };

  return threadId;
}

/**
 * Clears the cached thread (e.g., on logout, account switch, or error).
 */
export function clearThreadCache() {
  threadCache = { threadId: null, accountname: null, createdAt: null };
}

// ─── Cortex Agent Streaming ──────────────────────────────────────────────────

/**
 * Initiates a streaming Cortex Agent run using the HttpOnly session cookie.
 * sf-manager 2.1.0: sessionToken is no longer sent in the request body;
 * the session is identified by the sf:vision:session-id cookie (same-origin).
 * log_history: true tells sf-manager to write the chat-history entry server-side.
 *
 * @param {Object}  params
 * @param {Array}   params.messages    - conversation history in role/content format
 * @param {AbortSignal} [params.signal] - optional cancellation signal
 * @param {string}  params.accountname - billing account ID
 * @param {string}  [params.threadId]  - optional thread ID for conversation continuity
 * @returns {ReadableStreamDefaultReader}
 */
export async function streamCortexAgentSessionRun({ messages, signal, accountname, threadId, parentMessageId = 0 }) {
  const apiMessages = messages.map((msg) => ({
    role: msg.role,
    content: [{ type: "text", text: msg.content }],
  }));

  const body = {
    accountname,
    messages: apiMessages,
    stream: true,
    log_history: true,
    vertical: "vision",
    parent_message_id: parentMessageId,
  };

  // Include threadId if provided for conversation continuity
  if (threadId) {
    body.thread_id = threadId;
  }

  const response = await fetch(getCortexAgentSessionRunEndpoint(), {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Cortex Agent error ${response.status}: ${text}`);
  }

  return response.body.getReader();
}
